import { computed } from 'vue';
import { useRouter } from 'vue-router';

import { formStore } from '@/stores/form.js';
import { pakketStore } from '@/stores/pakket.js';
import { verzekeringStore } from '@/stores/verzekering.js';

export function useCoverageOptionsVoordeelpakket() {
  const router = useRouter();
  const { funnelEntry } = formStore();
  const { verzekeringExists } = verzekeringStore();

  // extra options for the voordeelpakket if funnelEntry is inboedel or woonhuis
  const secondOptions = () => {
    return [
      {
        soort: 'inboedel',
        titel: 'Inboedel',
        subtitel: 'Je spullen in huis verzekerd tegen diefstal en schade',
        kenmerk: '1',
        usps: [
          {
            usp: 'Diefstal van je inboedel',
            positive: true,
          },
          {
            usp: 'Brand en schade aan inboedel',
            positive: true,
          },
          {
            usp: 'Schade aan woning',
            positive: false,
          },
        ],
      },
      {
        soort: 'woonhuis',
        titel: 'Woonhuis',
        subtitel: 'Verzekert schade aan uw woning, garage, schuur of bijgebouw, inclusief glasdekking',
        kenmerk: '2',
        usps: [
          {
            usp: 'Schade aan woning',
            positive: true,
          },
          {
            usp: 'Inclusief glasdekking',
            positive: true,
          },
          {
            usp: 'Diefstal van je inboedel',
            positive: false,
          },
        ],
      },
      {
        soort: 'inboedelEnWoonhuis',
        titel: 'Inboedel & Woonhuis',
        subtitel: 'Verzekerd voor je huis en al je spullen in huis',
        kenmerk: '3',
        usps: [
          {
            usp: 'Schade aan woning + glasdekking',
            positive: true,
          },
          {
            usp: 'Brand en stormschade aan je huis',
            positive: true,
          },
          {
            usp: 'Brand en schade aan inboedel',
            positive: true,
          },
          {
            usp: 'Diefstal van je inboedel',
            positive: true,
          },
        ],
      },
    ];
  };

  const options = [
    {
      soort: 'inboedel',
      titel: 'Inboedel',
      subtitel: 'Je spullen in huis verzekerd tegen diefstal en schade',
      kenmerk: '1',
      usps: [
        {
          usp: 'Schade aan inboedel',
          positive: true,
        },
      ],
    },
    {
      soort: 'woonhuis',
      titel: 'Woonhuis',
      subtitel: 'Verzekert schade aan uw woning, garage, schuur of bijgebouw, inclusief glasdekking',
      kenmerk: '3',
      usps: [
        {
          usp: 'Schade aan woning',
          positive: true,
        },
      ],
    },
    {
      soort: 'aansprakelijkheid',
      titel: 'Aansprakelijkheidsverzekering',
      subtitel:
        'Met een aansprakelijkheidsverzekering ben je gedekt voor schade die je per ongeluk toe brengt aan een persoon of andermans spullen.',
      kenmerk: '3',
      usps: [
        {
          usp: 'Schade aan andermans spullen',
          positive: true,
        },
        {
          usp: 'Schade aan een persoon',
          positive: true,
        },
        {
          usp: 'Schade aan woning of inboedel',
          positive: false,
        },
      ],
    },
    {
      soort: 'rechtsbijstand',
      titel: 'Rechtsbijstand',
      subtitel:
        'Je bent gedekt voor geschillen binnen de EU over (online) aankopen van goederen en diensten, zoals problemen met leveranciers van energie, telefoon of internet.',
      kenmerk: '4',
      usps: [
        {
          usp: 'Rechtsbijstand',
          positive: true,
        },
      ],
    },
    {
      soort: 'doorlopendeReis',
      titel: 'Doorlopende reis',
      subtitel:
        'Deze verzekering vergoedt hulp, schade of kosten als je op reis bent. Je hebt keuze uit diverse aanvullende dekkingen, waaronder annulering.',
      kenmerk: '5',
      usps: [
        {
          usp: 'Doorlopende reis',
          positive: true,
        },
      ],
    },
    {
      soort: 'gezinsongevallen',
      titel: 'Gezinsongevallen',
      subtitel: 'Je bent gedekt als een gezinslid plotseling wordt getroffen door een onvrijwillig gebeurtenis van buitenaf.',
      kenmerk: '6',
      usps: [
        {
          usp: 'Gezinsongevallen',
          positive: true,
        },
      ],
    },
  ];

  const sortingArr = computed(() => {
    // Afhankelijk van de funnel entry kun je hier de volgorde bepalen
    switch (funnelEntry) {
      case 'aansprakelijkheid':
        return ['aansprakelijkheid', 'inboedel', 'woonhuis', 'rechtsbijstand', 'gezinsongevallen', 'doorlopendeReis'];
      case 'inboedel':
        return ['inboedel', 'woonhuis', 'rechtsbijstand', 'aansprakelijkheid', 'gezinsongevallen', 'doorlopendeReis'];
      case 'woonhuis':
        return ['woonhuis', 'inboedel', 'rechtsbijstand', 'aansprakelijkheid', 'gezinsongevallen', 'doorlopendeReis'];
      case 'rechtsbijstandwoning':
        return ['rechtsbijstand', 'woonhuis', 'inboedel', 'aansprakelijkheid', 'gezinsongevallen', 'doorlopendeReis'];
      case 'gezinsongevallen':
        return ['gezinsongevallen', 'inboedel', 'woonhuis', 'aansprakelijkheid', 'rechtsbijstand', 'doorlopendeReis'];
      case 'doorlopendeReis':
        return ['doorlopendeReis', 'inboedel', 'woonhuis', 'aansprakelijkheid', 'rechtsbijstand', 'gezinsongevallen'];
      default:
        return ['inboedel', 'woonhuis', 'aansprakelijkheid', 'rechtsbijstandwoning', 'doorlopendeReis', 'gezinsongevallen'];
    }
  });

  const coverageOptions = () => {
    return options.sort((a, b) => {
      return sortingArr.value.indexOf(a.soort) - sortingArr.value.indexOf(b.soort);
    });
  };

  const resetRoutes = async (funnelType) => {
    // Sla de huidige route op
    await router.isReady();

    // Verwijder alle huidige routes
    router.getRoutes().forEach((route) => {
      router.removeRoute(route.name);
    });

    // Importeer de nieuwe routes op basis van de nieuwe funnelType
    await import(`@/router/funnels/routes.${funnelType}.js`).then((module) => {
      const funnelRoutes = module.default.routes;
      // Voeg de nieuwe routes toe
      funnelRoutes.forEach((route) => {
        if (route.path.indexOf(funnelType)) {
          router.addRoute(route);
        }
      });
    });
  };

  const updateRoutes = () => {
    const allRoutes = router.getRoutes();
    const allOptions = coverageOptions().map((a) => a.soort);
    allRoutes.forEach((route) => {
      const name = route.name;
      if (allOptions.includes(name)) {
        if (!verzekeringExists(name)) {
          router.removeRoute(name);
        }
      }
    });
  };

  const removeRouteWithName = (routes, routeName) => {
    return routes.filter((route) => route.name !== routeName);
  };

  const setRouteArray = () => {
    const allRoutes = router.getRoutes();
    const form = formStore();
    const pakket = pakketStore();
    const verzekering = verzekeringStore();
    const { verzekeringExists } = verzekeringStore();
    let foundIsVoordeelpakket = false;

    let [beforeIsVoordeelpakket, isVoordeelpakket, afterIsVoordeelpakket] = allRoutes.reduce(
      (acc, route) => {
        if (route.meta.nextStepSubmitAction === 'is-voordeelpakket') {
          foundIsVoordeelpakket = true;
          acc[1].push(route);
        } else if (!foundIsVoordeelpakket) {
          acc[0].push(route);
        } else {
          acc[2].push(route);
        }
        return acc;
      },
      [[], [], []], // initialiseer de arrays
    );
    const allOptions = coverageOptions().map((a) => a.soort);

    if (isVoordeelpakket.length > 0) {
      isVoordeelpakket.sort((a, b) => {
        return allOptions.indexOf(a.name) - allOptions.indexOf(b.name);
      });

      const namesIsVoordeelpakket = isVoordeelpakket.map((route) => route.name);
      // Only save gegevens route if woonhuis or inboedel are selected
      if (verzekeringExists('Woonhuis') || verzekeringExists('Inboedel')) {
        // Save the routes for the multistep form
        const gegevensRoute = beforeIsVoordeelpakket.find((route) => route.name === 'gegevens').name;
        form.multistepRoutes = [gegevensRoute, ...namesIsVoordeelpakket];
      } else {
        form.multistepRoutes = namesIsVoordeelpakket;
      }

      // Remove gegevens route if woonhuis and inboedel are not selected
      if (!verzekeringExists('Woonhuis') && !verzekeringExists('Inboedel')) {
        beforeIsVoordeelpakket = removeRouteWithName(beforeIsVoordeelpakket, 'gegevens');
      }

      // Remove woning route is pakket is regulier and has no vereende
      // Keep route when pakket is geennee or has vereende maatschappij
      if (!pakket.getGeennee && !verzekering.getHasVereende) {
        afterIsVoordeelpakket = removeRouteWithName(afterIsVoordeelpakket, 'woning');
      }
    }

    const newAllRoutes = beforeIsVoordeelpakket.concat(isVoordeelpakket, afterIsVoordeelpakket);
    form.sortedRoutes = newAllRoutes.map((route) => route.name);

    // If there are no questions, remove the route from the sortedRoutes
    for (const key in form.questions) {
      const routeName = key
        .replace(/([a-z])([A-Z])/g, '$1-$2')
        .toLowerCase()
        .replace(/vragen/g, '-vragen');
      if (Array.isArray(form.questions[key]) && form.questions[key].length === 0) {
        form.removeRouteByName(routeName);
      }
    }
  };

  return { secondOptions, coverageOptions, resetRoutes, updateRoutes, setRouteArray };
}
